'use client'

import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { sanitizeHTML } from '@/lib/utils'
import Image from 'next/image'
import Link from 'next/link'

interface SecReviewProps {
	heading?: {
		title?: string
	}
	text?: {
		text?: string
	}
	button?: {
		href?: string
		label?: string
	}
	content_collection?: {
		id?: string
		reviews_id?: {
			rating?: number
			author?: string
			comment?: string
		}
	}[]
}

export const fields = [
	'heading.title',
	'text.text',
	'content_collection.id',
	'content_collection.reviews_id.rating',
	'content_collection.reviews_id.author',
	'content_collection.reviews_id.comment',
	'button.href',
	'button.label',
] as const

interface Review {
	id: number
	author: string
	rating: number
	text: string
}

export default function SecReview({ data }: { data: SecReviewProps }) {
	const [selectedReview, setSelectedReview] = useState<number | null>(null)
	const [currentSlide, setCurrentSlide] = useState(0)
	const [isAnimating, setIsAnimating] = useState(false)
	const [scrollPosition, setScrollPosition] = useState(0)
	const [isDragging, setIsDragging] = useState(false)
	const [needsScrollbar, setNeedsScrollbar] = useState(false)
	const [dragStartY, setDragStartY] = useState(0)
	const [startScrollTop, setStartScrollTop] = useState(0)
	const contentRef = useRef<HTMLDivElement>(null)
	const scrollbarRef = useRef<HTMLDivElement>(null)
	const thumbRef = useRef<HTMLDivElement>(null)
	const [isMobile, setIsMobile] = useState(false)
	const [isClient, setIsClient] = useState(false)

	useEffect(() => {
		setIsClient(true)
	}, [])

	const limit = isMobile ? 100 : 150

	const createShortText = (text: string) => {
		const limit = isMobile ? 100 : 150;
		return { 
			text: text.length > limit ? text.slice(0, limit).trim() + '...' : text,
			needsButton: text.length > limit
		};
	}

	const reviews = data?.content_collection?.map(item => ({
		id: parseInt(item.id || '0'),
		author: item.reviews_id?.author || '',
		rating: item.reviews_id?.rating || 0,
		text: item.reviews_id?.comment || ''
	})) || []

	const generateSlides = () => {
		if (isMobile) {
			return reviews;
		}
		const mainSlides = reviews.map(review => review);
		return [...mainSlides, mainSlides[0], mainSlides[1], mainSlides[2]];
	}

	const slides = generateSlides();
	const totalSlides = slides.length

	const renderStars = (rating: number) => {
		return (
			<div className='flex gap-[5.73px]'>
				{Array(5)
					.fill(0)
					.map((_, index) => (
						<svg
							key={index}
							width='19'
							height='17'
							viewBox='0 0 19 17'
							fill='none'
							xmlns='http://www.w3.org/2000/svg'
						>
							<path
								d='M8.89478 0.509276C9.08528 -0.0770125 9.91472 -0.0770136 10.1052 0.509275L11.7145 5.46221C11.7997 5.72441 12.0441 5.90193 12.3197 5.90193H17.5276C18.144 5.90193 18.4003 6.69077 17.9016 7.05312L13.6884 10.1142C13.4654 10.2762 13.372 10.5635 13.4572 10.8257L15.0665 15.7786C15.257 16.3649 14.586 16.8524 14.0873 16.4901L9.87405 13.429C9.65101 13.267 9.34899 13.267 9.12595 13.429L4.91274 16.4901C4.41401 16.8524 3.74298 16.3649 3.93347 15.7786L5.54278 10.8257C5.62797 10.5635 5.53464 10.2762 5.31161 10.1142L1.09839 7.05312C0.599661 6.69077 0.855972 5.90193 1.47243 5.90193H6.68026C6.95595 5.90193 7.20028 5.72441 7.28548 5.46221L8.89478 0.509276Z'
								fill='#d1c1b4'
								fillOpacity='0.88'
							/>
						</svg>
					))}
			</div>
		)
	}

	const handlePrevSlide = () => {
		if (isAnimating) return
		setIsAnimating(true)

		if (currentSlide <= 0) {
			setCurrentSlide(slides.length - 3)
		} else {
			setCurrentSlide(prev => prev - 1)
		}
	}

	const handleNextSlide = () => {
		if (isAnimating) return
		setIsAnimating(true)

		if (currentSlide >= slides.length - 3) {
			setCurrentSlide(0)
		} else {
			setCurrentSlide(prev => prev + 1)
		}
	}

	const handleAnimationComplete = () => {
		setIsAnimating(false)
	}

	const handleCloseFullReview = () => {
		setSelectedReview(null)
	}

	const renderTestimonialCard = (review: Review) => {
		const { text: displayText, needsButton } = createShortText(review.text);

		return (
			<div className="bg-white rounded-[10px] p-7 flex flex-col gap-2.5 h-[280px]">
				<div className="flex items-center gap-[13px]">
					{renderStars(review.rating)}
					<span className="text-[#060606] text-base font-medium">
						{review.rating.toFixed(1)}
					</span>
				</div>
				<h3 className="text-black text-base font-brandon font-medium">
					{review.author}
				</h3>
				<div className="flex flex-col flex-1">
					<p className="text-black text-base font-brandon leading-normal font-light line-clamp-4">
						{displayText}
					</p>
					{needsButton && (
						<button
							onClick={() => setSelectedReview(reviews.findIndex(r => r.id === review.id))}
							className="flex items-center gap-[7px] text-brown mt-auto pt-4 group"
						>
							<span className="text-base font-brandon font-medium">
								Read the full review
							</span>
							<svg
								width="29"
								height="8"
								viewBox="0 0 29 8"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M28.3536 4.45853C28.5488 4.26327 28.5488 3.94669 28.3536 3.75143L25.1716 0.569447C24.9763 0.374184 24.6597 0.374184 24.4645 0.569447C24.2692 0.764709 24.2692 1.08129 24.4645 1.27655L27.2929 4.10498L24.4645 6.93341C24.2692 7.12867 24.2692 7.44525 24.4645 7.64051C24.6597 7.83578 24.9763 7.83578 25.1716 7.64051L28.3536 4.45853ZM0 4.60498H28V3.60498H0V4.60498Z"
									fill="#B67D49"
								/>
							</svg>
						</button>
					)}
				</div>
			</div>
		);
	}

	useEffect(() => {
		if (selectedReview !== null && contentRef.current) {
			const needsScroll =
				contentRef.current.scrollHeight > contentRef.current.clientHeight
			setNeedsScrollbar(needsScroll)

			contentRef.current.scrollTop = 0
			setScrollPosition(0)

			if (contentRef.current.parentElement) {
				const textHeight = contentRef.current.scrollHeight
				const containerHeight = contentRef.current.clientHeight

				if (textHeight < containerHeight) {
					contentRef.current.style.height = `${textHeight}px`
					contentRef.current.style.maxHeight = `${textHeight}px`
				}
			}
		}
	}, [selectedReview])

	const handleScroll = () => {
		if (contentRef.current) {
			const { scrollTop, scrollHeight, clientHeight } = contentRef.current
			if (scrollHeight > clientHeight) {
				const scrollPercentage = Math.min(
					(scrollTop / (scrollHeight - clientHeight)) * 100,
					100
				)
				setScrollPosition(scrollPercentage)
			} else {
				setScrollPosition(0)
			}
		}
	}

	const handleScrollbarClick = (e: React.MouseEvent<HTMLDivElement>) => {
		if (
			e.target !== thumbRef.current &&
			contentRef.current &&
			scrollbarRef.current
		) {
			const { top, height } = scrollbarRef.current.getBoundingClientRect()
			const clickPosition = (e.clientY - top) / height
			const scrollHeight =
				contentRef.current.scrollHeight - contentRef.current.clientHeight
			contentRef.current.scrollTop = clickPosition * scrollHeight
		}
	}

	const handleScrollbarDragStart = (
		e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
	) => {
		setIsDragging(true)

		if ('clientY' in e) {
			setDragStartY(e.clientY)
		} else {
			setDragStartY(e.touches[0].clientY)
		}

		if (contentRef.current) {
			setStartScrollTop(contentRef.current.scrollTop)
		}

		document.body.style.userSelect = 'none'
	}

	const handleScrollbarDragEnd = () => {
		setIsDragging(false)
		document.body.style.userSelect = ''
	}

	const handleMouseMove = (e: MouseEvent) => {
		if (isDragging && contentRef.current && scrollbarRef.current) {
			e.preventDefault()
			const { height, top } = scrollbarRef.current.getBoundingClientRect()
			const deltaY = e.clientY - dragStartY
			const deltaRatio = deltaY / height
			const scrollHeight =
				contentRef.current.scrollHeight - contentRef.current.clientHeight

			const thumbSizePercent = Math.max(
				(contentRef.current.clientHeight / contentRef.current.scrollHeight) *
					100,
				10
			)

			const maxScrollTop = scrollHeight

			contentRef.current.scrollTop = Math.max(
				0,
				Math.min(startScrollTop + deltaRatio * scrollHeight, maxScrollTop)
			)
		}
	}

	const handleTouchMove = (e: TouchEvent) => {
		if (isDragging && contentRef.current && scrollbarRef.current) {
			const { height, top } = scrollbarRef.current.getBoundingClientRect()
			const deltaY = e.touches[0].clientY - dragStartY
			const deltaRatio = deltaY / height
			const scrollHeight =
				contentRef.current.scrollHeight - contentRef.current.clientHeight

			const thumbSizePercent = Math.max(
				(contentRef.current.clientHeight / contentRef.current.scrollHeight) *
					100,
				10
			)

			const maxScrollTop = scrollHeight

			contentRef.current.scrollTop = Math.max(
				0,
				Math.min(startScrollTop + deltaRatio * scrollHeight, maxScrollTop)
			)
		}
	}

	useEffect(() => {
		if (isDragging) {
			window.addEventListener('mousemove', handleMouseMove)
			window.addEventListener('mouseup', handleScrollbarDragEnd)
			window.addEventListener('touchmove', handleTouchMove)
			window.addEventListener('touchend', handleScrollbarDragEnd)
		}

		return () => {
			window.removeEventListener('mousemove', handleMouseMove)
			window.removeEventListener('mouseup', handleScrollbarDragEnd)
			window.removeEventListener('touchmove', handleTouchMove)
			window.removeEventListener('touchend', handleScrollbarDragEnd)
		}
	}, [isDragging, dragStartY, startScrollTop])

	useEffect(() => {
		const checkMobile = () => {
			setIsMobile(window.innerWidth < 460)
		}

		checkMobile()
		window.addEventListener('resize', checkMobile)
		return () => window.removeEventListener('resize', checkMobile)
	}, [])

	if (!isClient) {
		return null
	}

	return (
		<section
			id='testimonials'
			className='SecReview w-full py-16 md:pb-24 md:pt-26 overflow-hidden relative bg-[#bec3b7]'
		>
			<div className='container md:py-10 grid grid-cols-1 lg:grid-cols-[360px_1fr] 2xl:grid-cols-[450px_1fr] gap-0 md:gap-12'>
				<div className='box_txt max-md:pb-12'>
					<h2 className='mb-5'>{data?.heading?.title || ''}</h2>
					<div dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || '')} className="prose" />
				</div>
				<div className='box_slider overflow-x-clip'>
					<div className='flex items-start'>
						{selectedReview !== null && (
							<motion.div
								initial={{ x: -100, opacity: 0 }}
								animate={{ x: 0, opacity: 1 }}
								exit={{ x: -100, opacity: 0 }}
								transition={{ duration: 0.3 }}
								className='flex flex-col sld_card_pop_up fixed md:absolute top-0 left-0 h-full bg-white max-w-[500px] p-[30px] md:p-[50px] z-[9999] md:z-[9]'
							>
								<div className='flex justify-between items-center mb-6'>
									<h2 className='text-4xl md:text-5xl font-agopus text-black'>
										{data?.heading?.title || ''}
									</h2>
									<button onClick={handleCloseFullReview} className='text-3xl'>
										<svg
											xmlns='http://www.w3.org/2000/svg'
											width='29'
											height='30'
											viewBox='0 0 29 30'
											fill='none'
										>
											<path
												d='M26.937 1.13612L13.3716 14.4132C13.1333 14.6464 13 14.9592 13 15.2849C13 15.6105 13.1333 15.9233 13.3716 16.1565L26.937 29.4386C27.048 29.5473 27.1807 29.6338 27.3273 29.6928C27.4738 29.7518 27.6313 29.7822 27.7904 29.7822C27.9495 29.7822 28.107 29.7518 28.2536 29.6928C28.4001 29.6338 28.5328 29.5473 28.6438 29.4386C28.8722 29.2156 29 28.9162 29 28.6044C29 28.2926 28.8722 27.9932 28.6438 27.7702L15.8873 15.2849L28.6438 2.802C28.8714 2.5791 28.9987 2.28023 28.9987 1.96906C28.9987 1.65789 28.8714 1.35902 28.6438 1.13612C28.5328 1.02737 28.4001 0.940931 28.2536 0.881909C28.107 0.822888 27.9495 0.79248 27.7904 0.79248C27.6313 0.79248 27.4738 0.822888 27.3273 0.881909C27.1807 0.940931 27.048 1.02737 26.937 1.13612Z'
												fill='#919985'
											/>
											<path
												d='M2.06299 1.13612L15.6284 14.4132C15.8667 14.6464 16 14.9592 16 15.2849C16 15.6105 15.8667 15.9233 15.6284 16.1565L2.06299 29.4386C1.95201 29.5473 1.81931 29.6338 1.67274 29.6928C1.52617 29.7518 1.36869 29.7822 1.20959 29.7822C1.0505 29.7822 0.893017 29.7518 0.746444 29.6928C0.59987 29.6338 0.467176 29.5473 0.356192 29.4386C0.127842 29.2156 0 28.9162 0 28.6044C0 28.2926 0.127842 27.9932 0.356192 27.7702L13.1127 15.2849L0.356192 2.802C0.128626 2.5791 0.00127888 2.28023 0.00127888 1.96906C0.00127888 1.65789 0.128626 1.35902 0.356192 1.13612C0.467176 1.02737 0.59987 0.940931 0.746444 0.881909C0.893017 0.822888 1.0505 0.79248 1.20959 0.79248C1.36869 0.79248 1.52617 0.822888 1.67274 0.881909C1.81931 0.940931 1.95201 1.02737 2.06299 1.13612Z'
												fill='#919985'
											/>
										</svg>
									</button>
								</div>
								<div className='flex flex-col'>
									<div className='mb-6'>
										<div className='flex items-center gap-[13px]'>
											{Array(5)
												.fill(0)
												.map((_, index) => (
													<svg
														key={index}
														width='19'
														height='17'
														viewBox='0 0 19 17'
														fill='none'
														xmlns='http://www.w3.org/2000/svg'
													>
														<path
															d='M8.89478 0.509276C9.08528 -0.0770125 9.91472 -0.0770136 10.1052 0.509275L11.7145 5.46221C11.7997 5.72441 12.0441 5.90193 12.3197 5.90193H17.5276C18.144 5.90193 18.4003 6.69077 17.9016 7.05312L13.6884 10.1142C13.4654 10.2762 13.372 10.5635 13.4572 10.8257L15.0665 15.7786C15.257 16.3649 14.586 16.8524 14.0873 16.4901L9.87405 13.429C9.65101 13.267 9.34899 13.267 9.12595 13.429L4.91274 16.4901C4.41401 16.8524 3.74298 16.3649 3.93347 15.7786L5.54278 10.8257C5.62797 10.5635 5.53464 10.2762 5.31161 10.1142L1.09839 7.05312C0.599661 6.69077 0.855972 5.90193 1.47243 5.90193H6.68026C6.95595 5.90193 7.20028 5.72441 7.28548 5.46221L8.89478 0.509276Z'
															fill='#DAC79B'
															fillOpacity='0.88'
														/>
													</svg>
												))}
											<span className='text-[#060606] text-base font-medium'>
												{reviews[selectedReview].rating.toFixed(1)}
											</span>
										</div>
									</div>
									<div className='mb-6 mt-4 sec_home_review_text_pop_up relative'>
										<div
											className='relative'
											style={{
												height: '300px',
												maxHeight: '300px',
												overflow: 'hidden',
											}}
										>
											<div
												ref={contentRef}
												className='h-full overflow-y-auto'
												style={{
													overflowX: 'hidden',
													scrollbarWidth: 'none',
													msOverflowStyle: 'none',
													paddingRight: '1rem',
												}}
												onScroll={handleScroll}
											>
												<p className='text-black text-base font-brandon leading-relaxed font-light'>
													{reviews[selectedReview].text}
												</p>
												<style jsx>{`
													div::-webkit-scrollbar {
														display: none;
													}
												`}</style>
											</div>

											{needsScrollbar && (
												<div
													ref={scrollbarRef}
													className='absolute top-0 right-0 w-2 h-full bg-gray-100 rounded cursor-pointer'
													onClick={handleScrollbarClick}
												>
													<div
														ref={thumbRef}
														className='absolute w-full rounded cursor-grab active:cursor-grabbing hover:bg-brown transition-colors'
														style={{
															top: `${Math.min(
																scrollPosition,
																100 -
																	(contentRef.current &&
																	contentRef.current.scrollHeight > 0
																		? Math.max(
																				(contentRef.current.clientHeight /
																					contentRef.current.scrollHeight) *
																					100,
																				10
																		  )
																		: 20)
															)}%`,
															height:
																contentRef.current &&
																contentRef.current.scrollHeight > 0
																	? `${Math.max(
																			(contentRef.current.clientHeight /
																				contentRef.current.scrollHeight) *
																				100,
																			10
																	  )}%`
																	: '20%',
															backgroundColor: '#B67D49',
															minHeight: '30px',
															maxHeight: '90%',
														}}
														onMouseDown={e => {
															e.stopPropagation()
															handleScrollbarDragStart(e)
														}}
														onTouchStart={e => {
															e.stopPropagation()
															handleScrollbarDragStart(e)
														}}
													/>
												</div>
											)}
										</div>
									</div>
									<div className='mb-4'>
										<p className='font-brandon font-medium'>
											{reviews[selectedReview].author}
										</p>
									</div>
								</div>
							</motion.div>
						)}

						<div className={`flex-1 ${selectedReview !== null ? '' : ''}`}>
							<div className={isMobile ? 'sld relative overflow-x-clip' : 'sld relative -mx-4'}>
								<motion.div
									className='flex'
									animate={
										isMobile
											? { x: `-${currentSlide * 92}vw` }
											: { x: `-${currentSlide * 400}px` }
									}
									transition={{
										duration: 0.5,
										ease: 'easeInOut',
									}}
									onAnimationComplete={handleAnimationComplete}
								>
									{slides.map((review, index) => (
										<div
											key={index}
											className={
												isMobile
													? 'w-[92vw] max-w-[22rem] mx-auto px-0 flex-shrink-0'
													: 'w-[300px] md:w-[350px] px-4'
											}
										>
											{renderTestimonialCard(review)}
										</div>
									))}
								</motion.div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div className='container mt-12 md:flex md:flex-row-reverse'>
				<div className='flex justify-center md:justify-end items-center gap-6 md:gap-[158px] md:w-1/2 max-md:mb-12'>
					<button
						onClick={handlePrevSlide}
						disabled={isAnimating}
						className='flex items-center justify-center hover:opacity-70 transition-opacity disabled:opacity-50'
						aria-label='Previous slide'
					>
						<svg
							xmlns='http://www.w3.org/2000/svg'
							width='16'
							height='26'
							viewBox='0 0 16 26'
							fill='none'
							className='group rotate-180'
						>
							<path
								d='M14.6969 14.4733C15.3676 13.8026 15.3676 12.715 14.6969 12.0442L3.76582 1.11319C3.09504 0.442409 2.00748 0.442409 1.3367 1.11319C0.665917 1.78397 0.665917 2.87153 1.3367 3.54231L11.0532 13.2588L1.3367 22.9753C0.665916 23.646 0.665915 24.7336 1.3367 25.4044C2.00748 26.0752 3.09503 26.0752 3.76582 25.4044L14.6969 14.4733ZM11.7646 14.9764L13.4823 14.9764L13.4823 11.5411L11.7646 11.5411L11.7646 14.9764Z'
								fill='#000000'
								className='md:group-hover:fill-[#B67D49] md:transition-colors fill-[#9E6735] md:fill-[#000000]'
							/>
						</svg>
					</button>
					<div className='md:hidden text-center text-base font-brandon text-[#9E6735] uppercase text-lg font-medium tracking-widest txt_swiper_arrow'></div>
					<button
						onClick={handleNextSlide}
						disabled={isAnimating}
						className='flex items-center justify-center hover:opacity-70 transition-opacity disabled:opacity-50'
						aria-label='Next slide'
					>
						<svg
							xmlns='http://www.w3.org/2000/svg'
							width='16'
							height='26'
							viewBox='0 0 16 26'
							fill='none'
							className='group'
						>
							<path
								d='M14.6969 14.4733C15.3676 13.8026 15.3676 12.715 14.6969 12.0442L3.76582 1.11319C3.09504 0.442409 2.00748 0.442409 1.3367 1.11319C0.665917 1.78397 0.665917 2.87153 1.3367 3.54231L11.0532 13.2588L1.3367 22.9753C0.665916 23.646 0.665915 24.7336 1.3367 25.4044C2.00748 26.0752 3.09503 26.0752 3.76582 25.4044L14.6969 14.4733ZM11.7646 14.9764L13.4823 14.9764L13.4823 11.5411L11.7646 11.5411L11.7646 14.9764Z'
								fill='#000000'
								className='md:group-hover:fill-[#B67D49] md:transition-colors fill-[#9E6735] md:fill-[#000000]'
							/>
						</svg>
					</button>
				</div>
				{data?.button && (
					<div className='flex md:w-1/2 max-md:justify-center'>
						<Link
							href={data?.button?.href || ''}
							className='btn'
						>{data?.button?.label || ''}
						</Link>
					</div>
				)}
			</div>
		</section>
	)
}
